<template>
  <div class="ip-address-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <h1 class="hero-title">My IP Address</h1>
            <p class="hero-subtitle">
              Discover your public IPv4 and IPv6 addresses instantly. 
              Useful for network configuration, troubleshooting, and security checks.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- IP Display Section -->
    <section class="ip-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-8">
            <!-- Loading State -->
            <div v-if="loading" class="loading-container">
              <div class="spinner"></div>
              <p>Detecting your IP addresses...</p>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="error-container">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <h3>Failed to detect IP addresses</h3>
              <p>{{ error }}</p>
              <button class="btn-retry" @click="fetchIpAddresses">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                </svg>
                Try Again
              </button>
            </div>

            <!-- IP Addresses Display -->
            <div v-else class="ip-cards">
              <!-- IPv4 Card -->
              <div v-if="ipv4" class="ip-card ipv4-card">
                <div class="ip-header">
                  <div class="ip-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
                      <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
                      <line x1="6" y1="6" x2="6.01" y2="6"/>
                      <line x1="6" y1="18" x2="6.01" y2="18"/>
                    </svg>
                  </div>
                  <div class="ip-label">
                    <h3>IPv4 Address</h3>
                    <span class="ip-version">Version 4</span>
                  </div>
                </div>
                <div class="ip-value-container">
                  <code class="ip-value">{{ ipv4 }}</code>
                  <button 
                    class="btn-copy" 
                    :class="{ 'copied': ipv4Copied }"
                    @click="copyToClipboard(ipv4, 'ipv4')"
                    title="Copy to clipboard"
                  >
                    <svg v-if="!ipv4Copied" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- IPv6 Card -->
              <div v-if="ipv6" class="ip-card ipv6-card">
                <div class="ip-header">
                  <div class="ip-icon ipv6-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
                      <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
                      <line x1="6" y1="6" x2="6.01" y2="6"/>
                      <line x1="6" y1="18" x2="6.01" y2="18"/>
                      <line x1="10" y1="6" x2="10.01" y2="6"/>
                      <line x1="10" y1="18" x2="10.01" y2="18"/>
                    </svg>
                  </div>
                  <div class="ip-label">
                    <h3>IPv6 Address</h3>
                    <span class="ip-version ipv6-version">Version 6</span>
                  </div>
                </div>
                <div class="ip-value-container">
                  <code class="ip-value ipv6-value">{{ ipv6 }}</code>
                  <button 
                    class="btn-copy" 
                    :class="{ 'copied': ipv6Copied }"
                    @click="copyToClipboard(ipv6, 'ipv6')"
                    title="Copy to clipboard"
                  >
                    <svg v-if="!ipv6Copied" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- No IPv6 Notice -->
              <div v-if="ipv4 && !ipv6" class="no-ipv6-notice">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                <div>
                  <strong>IPv6 not available</strong>
                  <p>Your network or ISP may not support IPv6 connectivity.</p>
                </div>
              </div>

              <!-- Refresh Button -->
              <div class="refresh-container">
                <button class="btn-refresh" @click="fetchIpAddresses">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                    <path d="M21 3v5h-5" />
                  </svg>
                  Refresh IP Addresses
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Info Section -->
    <section class="info-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-8">
            <div class="info-card">
              <h3>About IP Addresses</h3>
              <p>
                An IP (Internet Protocol) address is a unique numerical label assigned to each device 
                connected to a computer network. It serves two main purposes: network interface identification 
                and location addressing.
              </p>
              
              <h4>IPv4 vs IPv6</h4>
              <div class="comparison-grid">
                <div class="comparison-item">
                  <strong>IPv4</strong>
                  <ul>
                    <li>32-bit address (4 bytes)</li>
                    <li>Format: xxx.xxx.xxx.xxx</li>
                    <li>Example: 192.168.1.1</li>
                    <li>~4.3 billion addresses</li>
                    <li>Most widely used</li>
                  </ul>
                </div>
                <div class="comparison-item">
                  <strong>IPv6</strong>
                  <ul>
                    <li>128-bit address (16 bytes)</li>
                    <li>Format: xxxx:xxxx:xxxx:xxxx:xxxx:xxxx:xxxx:xxxx</li>
                    <li>Example: 2001:0db8:85a3::8a2e:0370:7334</li>
                    <li>340 undecillion addresses</li>
                    <li>Future-proof solution</li>
                  </ul>
                </div>
              </div>
              
              <h4>Common Use Cases</h4>
              <ul>
                <li><strong>Network Configuration:</strong> Setting up routers, servers, and network devices</li>
                <li><strong>Remote Access:</strong> Connecting to systems remotely (SSH, RDP, VPN)</li>
                <li><strong>Security:</strong> Whitelisting/blacklisting IPs, firewall configuration</li>
                <li><strong>Troubleshooting:</strong> Diagnosing network connectivity issues</li>
                <li><strong>Geolocation:</strong> Approximate geographical location services</li>
                <li><strong>Web Analytics:</strong> Tracking visitor statistics and preventing fraud</li>
              </ul>
              
              <h4>Privacy & Security</h4>
              <p>
                Your IP address can reveal your approximate location and ISP. For enhanced privacy, 
                consider using a VPN (Virtual Private Network) or proxy service. Be cautious about 
                sharing your IP address publicly, as it can be used to target attacks or track your online activity.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

useHead({
  title: 'My IP Address | Developer Tools',
  meta: [
    { name: 'description', content: 'Check your public IPv4 and IPv6 addresses instantly. Free tool for network troubleshooting and configuration.' }
  ]
})

const ipv4 = ref<string>('')
const ipv6 = ref<string>('')
const loading = ref(true)
const error = ref<string>('')
const ipv4Copied = ref(false)
const ipv6Copied = ref(false)

const fetchIpAddresses = async () => {
  loading.value = true
  error.value = ''
  ipv4.value = ''
  ipv6.value = ''
  
  try {
    // Fetch IPv4
    try {
      const ipv4Response = await fetch('https://api.ipify.org?format=json')
      if (ipv4Response.ok) {
        const ipv4Data = await ipv4Response.json()
        ipv4.value = ipv4Data.ip
      }
    } catch (err) {
      console.error('IPv4 fetch error:', err)
    }
    
    // Fetch IPv6
    try {
      const ipv6Response = await fetch('https://api64.ipify.org?format=json')
      if (ipv6Response.ok) {
        const ipv6Data = await ipv6Response.json()
        // Check if it's actually an IPv6 address (contains colons)
        if (ipv6Data.ip && ipv6Data.ip.includes(':')) {
          ipv6.value = ipv6Data.ip
        }
      }
    } catch (err) {
      console.error('IPv6 fetch error:', err)
      // IPv6 not available is not an error condition
    }
    
    if (!ipv4.value && !ipv6.value) {
      error.value = 'Could not detect any IP addresses. Please check your internet connection.'
    }
  } catch (err) {
    error.value = 'An unexpected error occurred while fetching IP addresses.'
    console.error('IP fetch error:', err)
  } finally {
    loading.value = false
  }
}

const copyToClipboard = async (text: string, type: 'ipv4' | 'ipv6') => {
  try {
    await navigator.clipboard.writeText(text)
    
    if (type === 'ipv4') {
      ipv4Copied.value = true
      setTimeout(() => {
        ipv4Copied.value = false
      }, 2000)
    } else {
      ipv6Copied.value = true
      setTimeout(() => {
        ipv6Copied.value = false
      }, 2000)
    }
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

onMounted(() => {
  fetchIpAddresses()
})
</script>

<style scoped>
.ip-address-page {
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
}

.hero-section {
  padding: 4rem 0 2rem;
  text-align: center;
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #4a5568;
  max-width: 600px;
  margin: 0 auto;
}

.ip-section {
  padding: 2rem 0 4rem;
}

.loading-container {
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.spinner {
  border: 4px solid #f3f4f6;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-container p {
  color: #718096;
  font-size: 1.1rem;
}

.error-container {
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.error-container svg {
  color: #f56565;
  margin-bottom: 1rem;
}

.error-container h3 {
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.error-container p {
  color: #718096;
  margin-bottom: 1.5rem;
}

.btn-retry {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn-retry:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.ip-cards {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.ip-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 2px solid #e2e8f0;
  transition: all 0.3s ease;
}

.ip-card:hover {
  border-color: #667eea;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.15);
}

.ipv6-card:hover {
  border-color: #48bb78;
  box-shadow: 0 4px 16px rgba(72, 187, 120, 0.15);
}

.ip-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.ip-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ipv6-icon {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
}

.ip-label h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 0.25rem 0;
}

.ip-version {
  display: inline-block;
  background: #e2e8f0;
  color: #4a5568;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.ipv6-version {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
}

.ip-value-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #f7fafc;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  border: 2px solid #e2e8f0;
}

.ip-value {
  font-family: 'Courier New', monospace;
  font-size: 1.25rem;
  color: #2d3748;
  font-weight: 600;
  flex: 1;
  word-break: break-all;
}

.ipv6-value {
  font-size: 1rem;
}

.btn-copy {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 0.6rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #4a5568;
  flex-shrink: 0;
}

.btn-copy:hover {
  border-color: #667eea;
  color: #667eea;
  background: #f7fafc;
}

.btn-copy.copied {
  border-color: #48bb78;
  color: #48bb78;
  background: #f0fff4;
}

.no-ipv6-notice {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: #fef5e7;
  border: 2px solid #f39c12;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  color: #7d6608;
}

.no-ipv6-notice svg {
  flex-shrink: 0;
  margin-top: 0.15rem;
}

.no-ipv6-notice strong {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 1rem;
}

.no-ipv6-notice p {
  margin: 0;
  font-size: 0.9rem;
  color: #8b7505;
}

.refresh-container {
  text-align: center;
  margin-top: 1rem;
}

.btn-refresh {
  background: white;
  border: 2px solid #e2e8f0;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #4a5568;
  transition: all 0.2s ease;
}

.btn-refresh:hover {
  border-color: #667eea;
  color: #667eea;
  background: #f7fafc;
}

.info-section {
  padding: 4rem 0;
  background: white;
}

.info-card {
  padding: 2rem;
}

.info-card h3 {
  color: #1a202c;
  font-weight: 700;
  margin-bottom: 1rem;
  font-size: 1.75rem;
}

.info-card h4 {
  color: #2d3748;
  font-weight: 600;
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-size: 1.25rem;
}

.info-card p {
  color: #4a5568;
  line-height: 1.7;
  margin-bottom: 1rem;
}

.info-card ul {
  color: #4a5568;
  line-height: 1.8;
  margin-bottom: 1rem;
}

.comparison-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 1.5rem 0;
}

.comparison-item {
  background: #f7fafc;
  padding: 1.5rem;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
}

.comparison-item strong {
  display: block;
  color: #1a202c;
  font-size: 1.1rem;
  margin-bottom: 1rem;
}

.comparison-item ul {
  margin: 0;
  padding-left: 1.25rem;
}

.comparison-item li {
  margin-bottom: 0.5rem;
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1rem;
  }
  
  .ip-value {
    font-size: 1rem;
  }
  
  .ipv6-value {
    font-size: 0.85rem;
  }
  
  .comparison-grid {
    grid-template-columns: 1fr;
  }
}
</style>
